import React, { useState, useEffect, useRef } from 'react';
import moment from 'moment';
import Summary from '../components/Summary';
import Select from 'react-select';
import WeeklyTable from '../components/Tables/Weekly/WeeklyTable';
import ToggleTable from '../components/Tables/Weekly/ToggleTable';
import ExecutiveTable from '../components/Tables/Weekly/ExecutiveTable';
import InvoicingTable from '../components/Tables/Weekly/InvoicingTable';
import WeeklyDatePicker from '../../payments/paymentsComponents/WeeklyDatePicker';
import DateRangePicker from '../../payment_platform/DateRangePicker';
import { DEFAULT_PRESET } from '../../payment_platform/DateRangePicker/presets';
import ViewToggle from '../../payment_platform/ViewToggle';
import { toDateObjs, toStrings } from '../components/helpers/paymentsHelper';
import {DownArrow, SearchIcon, RefreshIcon, ICON_GREY} from '../components/helpers/svgHelper';
import { IconButton, PP_REFRESH_GREY } from '../components/helpers/paymentControls';
import '../styles/weekly-header.css';

// Inline heroicons used by the flag-on header and the ViewToggle. The dollar and
// briefcase icons inherit `currentColor`, so they read white on the collapsed
// purple segment and #745D96 on the expanded white segment.
const DollarCircleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path fillRule="evenodd" clipRule="evenodd" d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm.75 4a.75.75 0 00-1.5 0v.616a2.75 2.75 0 00.25 5.384h1a1.25 1.25 0 010 2.5H9.5a.75.75 0 000 1.5h1.75V17a.75.75 0 001.5 0v-.616a2.75 2.75 0 00-.25-5.384h-1a1.25 1.25 0 010-2.5H14.5a.75.75 0 000-1.5h-1.75V6z" />
  </svg>
);

const BriefcaseIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
    <path fillRule="evenodd" clipRule="evenodd" d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z" />
    <path d="M2 13.692V16a2 2 0 002 2h12a2 2 0 002-2v-2.308A24.974 24.974 0 0110 15c-2.796 0-5.487-.46-8-1.308z" />
  </svg>
);

function Weekly({
  selectStyles,
  data,
  setData,
  handleChange,
  comparisonOptions,
  fetchCompanies,
  week,
  setWeek,
  consolidatedTotals,
  loading,
  selectedComparison,
  setComparisonPeriod,
  paymentPlatformMvp,
  tabView,
  setTabView,
  onSearch = () => {},
  onDownloadRow = () => {}
}) {
  const [dropdown, setDropdown] = useState(false);
  const [search, setSearch] = useState('');

  // Server-side in-table search: debounce keystrokes ~350ms, then hand the query
  // up so Payments can re-fetch weekly_summary with `q`. The first render is
  // skipped so mounting does not trigger a redundant fetch.
  const isFirstSearch = useRef(true);
  useEffect(() => {
    if (isFirstSearch.current) {
      isFirstSearch.current = false;
      return undefined;
    }
    const handle = setTimeout(() => onSearch(search), 350);
    return () => clearTimeout(handle);
  }, [search]);

  // Load the default range on arrival. The date picker already defaults to this finance week,
  // so making the user press Search to see it was asking them to confirm a choice they had not
  // made. Runs once on mount; every later fetch is driven by Search, the debounce above, or a
  // date change.
  useEffect(() => {
    fetchCompanies();
  }, []);

  
  const resetFilters = () => {
    setData([]);
  };

  // Flag-on reset: restore every filter to its default. The finance week is the
  // current Monday–Sunday (mirroring the Rails controller's
  // Date.today.beginning_of_week/end_of_week), the period returns to the picker's
  // default ('this_week', which compares against the previous week), and the
  // loaded rows are cleared.
  const resetAllFilters = () => {
    setWeek({
      from: moment().startOf('isoWeek').format('DD/MM/YYYY'),
      to: moment().endOf('isoWeek').format('DD/MM/YYYY'),
    });
    setComparisonPeriod(DEFAULT_PRESET);
    // Clear the query as well as the box. Reset previously emptied `data` only, so the
    // next fetch still carried the held q and reset appeared to re-show the company the
    // user was trying to get away from. onSearch is called directly rather than relying
    // on the debounce, because the box can already be empty while the held query is not.
    setSearch('');
    onSearch('');
    setData([]);
  };

  const viewToggleOptions = [
    { key: 'invoicing', label: 'Invoicing View', icon: <DollarCircleIcon /> },
    { key: 'executive', label: 'Executive View', icon: <BriefcaseIcon /> },
  ];

  return (
    <div className="bg-transparent pt-2 pb-4 space-y-4">

      {loading && (
        <div className="fixed top-0 left-0 right-0 bottom-0 w-full h-screen z-50 overflow-hidden bg-gray-700 opacity-50 flex flex-col items-center justify-center">
          <div className="loader ease-linear rounded-full border-4 border-t-4 h-12 w-12 mb-4"></div>
        </div>
      )}

      {paymentPlatformMvp ? (
        <div className="flex flex-row items-center" style={{ gap: '10px' }}>
          <div className="pp-header-date" style={{ flex: 1 }}>
            <DateRangePicker
              value={toDateObjs(week)}
              onChange={(range) => { setWeek(toStrings(range)); setComparisonPeriod(range.preset); }}
              maxDate={new Date()}
              loading={loading}
            />
          </div>

          <IconButton
            testId="weekly-search-button"
            title="Search"
            disabled={loading}
            onClick={() => fetchCompanies()}
            icon={<SearchIcon size={20} />}
          />

          <IconButton
            testId="weekly-refresh-button"
            title="Reset filters"
            disabled={data.length === 0}
            onClick={resetAllFilters}
            fill={PP_REFRESH_GREY}
            icon={<RefreshIcon color={ICON_GREY} size={20} />}
          />

          <ViewToggle
            options={viewToggleOptions}
            value={tabView ? 'executive' : 'invoicing'}
            onChange={(key) => setTabView(key === 'executive')}
            disabled={data.length === 0}
            ariaLabel="Payment view"
          />
        </div>
      ) : (
        <div className="flex flex-row justify-between items-center">
          <div className="flex flex-row items-center space-x-2">
            <div className="relative inline-block text-left">
              <div>
                <button
                  type="button"
                  style={{ width: '150px' }}
                  onClick={() => setDropdown(!dropdown)}
                  className="inline-flex w-full justify-between gap-x-1.5 border-gray-400 border rounded-md bg-white text-gray-700 px-3 py-2 text-xxs font-thin shadow-sm"
                >
                  {week.from} - {week.to}
                  <DownArrow />
                </button>
              </div>
              {dropdown && <WeeklyDatePicker setWeek={setWeek} week={week} dropdown={dropdown} setDropdown={setDropdown} />}
            </div>

            {tabView &&
              (
                <Select
                  value={selectedComparison}
                  onChange={(option) => handleChange(option)}
                  options={comparisonOptions}
                  placeholder="Comparison Period"
                  styles={selectStyles}
                />
              )
            }

            <div className="flex space-x-2" style={{ height: '40px' }}>
              <button
                type="button"
                disabled={selectedComparison === null}
                onClick={() => fetchCompanies()}
                title="Search"
                aria-label="Search"
                data-testid="weekly-search-button"
                className={selectedComparison === null ?
                  "w-10 h-10 bg-gray-400 text-gray-600 cursor-not-allowed rounded-lg flex items-center justify-center" :
                  "w-10 h-10 bg-purple-600 text-white rounded-lg flex items-center justify-center"}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 105.4 5.4a7.5 7.5 0 0011.25 11.25z" />
                </svg>
              </button>

              <button
                type="button"
                onClick={resetFilters}
                title="Refresh"
                aria-label="Refresh"
                data-testid="weekly-refresh-button"
                className="w-10 h-10 border border-gray-300 text-gray-600 rounded-lg flex items-center justify-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
                </svg>
              </button>
            </div>
          </div>

          <div className="flex flex-row">
            <button
              type="button"
              disabled={data.length === 0}
              onClick={() => setTabView(!tabView)}
              data-testid="weekly-view-switcher-pill"
              className={data.length === 0 ?
                      "h-9 px-3 bg-gray-400 text-gray-600 cursor-not-allowed rounded-full shadow-xl flex items-center justify-center gap-x-1.5 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:ring-opacity-75"
                      : "h-9 px-3 bg-purple-600 hover:bg-purple-700 text-white rounded-full shadow-xl flex items-center justify-center gap-x-1.5 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:ring-opacity-75"}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m3-9.5c0-1.38-1.343-2.5-3-2.5s-3 1.12-3 2.5 1.343 2.5 3 2.5 3 1.12 3 2.5-1.343 2.5-3 2.5-3-1.12-3-2.5" />
              </svg>
              <span className="font-normal text-sm">{tabView ? 'Executive View' : 'Invoicing View'}</span>
            </button>
          </div>
        </div>
      )}

      {/* Legacy (flag-off) top-of-page aggregator. The flag-on MVP hides it per the
          2026-07-23 demo, so it is gated to the legacy path only. */}
      {!paymentPlatformMvp && data.length > 0 && (
        <div className="w-full flex mt-2">
          <Summary title="Consolidated Totals" breakdown={consolidatedTotals} />
        </div>
      )}

      {/* The search area sits outside the data-length gate: a no-match server
          search empties `data`, and the input holding the query must stay
          mounted so the user can clear or refine it. */}
      {paymentPlatformMvp && (data.length > 0 || search !== '') && (
            <div className="overflow-y-auto px-2 scrollbar" style={{ maxHeight: '800px' }}>
              <div className="px-1 mb-2" style={{ maxWidth: '220px' }}>
                <div className="relative flex items-center">
                  <input
                    type="text"
                    placeholder="Search"
                    data-testid="weekly-table-search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{ paddingTop: '0.44rem', paddingBottom: '0.44rem' }}
                    className="w-full pl-4 pr-10 text-gray-800 bg-white border border-gray-300 rounded-lg focus:outline-none placeholder-gray-400 placeholder-opacity-100 font-light text-sm"
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                </div>
              </div>

              {data.length > 0 && tabView && (
                <ExecutiveTable data={data} />
              )}
              {data.length > 0 && !tabView && (
                <InvoicingTable data={data} onDownloadRow={onDownloadRow} />
              )}
              {data.length === 0 && (
                <p className="px-1 py-4 text-sm text-gray-500" data-testid="weekly-search-empty">
                  No companies match your search for this date range.
                </p>
              )}
            </div>
      )}

      {!paymentPlatformMvp && data.length > 0 && (
        tabView ? (
          <div className="overflow-y-auto px-2 scrollbar" style={{ maxHeight: '800px' }}>
            <WeeklyTable data={data} selectedComparison={selectedComparison} />
          </div>
        ) : (
          <div className="overflow-y-auto px-2 scrollbar" style={{ maxHeight: '800px' }}>
            <ToggleTable data={data} />
          </div>
        )
      )}
    </div>
  );
}

export default Weekly;
